/**
 * Reports Page
 */
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
  Business as ServicesIcon,
  MedicalServices as DoctorsIcon,
  Insights as OverviewIcon,
  Speed as OperationsIcon,
  PersonSearch as PatientsIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
  useGetFinancialReportQuery,
  useLazyExportFinancialReportQuery,
} from '../../store/api/reportsApi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/usePermissions';
import { DoctorsReport, PatientsReport, ServicesPerformanceReport } from '../../components/reports/DetailedReports';
import {
  CustomersInsights,
  MoneySummary,
  OperationsReport,
  OverviewReport,
} from '../../components/reports/ManagementReports';
import { periodPresets } from '../../utils/reportPeriods';
import FinancialSections from '../../components/reports/FinancialSections';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportsPage = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canExport = hasPermission('export_reports');
  // Tab, period and filter live in the URL so "Back" from an order returns to the same report
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = Number(searchParams.get('tab')) || 0;
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';
  const providerType = searchParams.get('provider_type') ?? '';
  const updateParams = (changes: Record<string, string | number>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) =>
          value === '' || value === 0 ? next.delete(key) : next.set(key, String(value)),
        );
        return next;
      },
      { replace: true },
    );
  const setDateFrom = (value: string) => updateParams({ from: value });
  const setDateTo = (value: string) => updateParams({ to: value });
  const setProviderType = (value: string) => updateParams({ provider_type: value });
  const setTabValue = (value: number) => updateParams({ tab: value });

  // Financial Report
  const { data: financialData, isLoading: financialLoading } = useGetFinancialReportQuery({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });
  const [exportFinancial, { isLoading: exportingFinancial }] = useLazyExportFinancialReportQuery();

  const period = { date_from: dateFrom || undefined, date_to: dateTo || undefined };

  const handleExport = async () => {
    try {
      const blob = await exportFinancial({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financial_report_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('reports.toast.exportSuccess'));
    } catch (error) {
      toast.error(t('reports.toast.exportError'));
      console.error('Export error:', error);
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('reports.title')}
        subtitle={t('reports.subtitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.reports') },
        ]}
      />

      {/* Date Range Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('reports.filters.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {periodPresets().map((preset) => {
              const selected = dateFrom === preset.from && dateTo === preset.to;
              return (
                <Chip
                  key={preset.key}
                  label={t(`reports.periods.${preset.key}`)}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => updateParams({ from: preset.from, to: preset.to })}
                />
              );
            })}
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label={t('reports.filters.dateFrom')}
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label={t('reports.filters.dateTo')}
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            {tabValue === 3 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('reports.filters.providerType')}</InputLabel>
                  <Select
                    value={providerType}
                    label={t('reports.filters.providerType')}
                    onChange={(e) => setProviderType(e.target.value)}
                  >
                    <MenuItem value="">{t('reports.filters.allTypes')}</MenuItem>
                    <MenuItem value="SPECIALIST">{t('reports.providerType.SPECIALIST')}</MenuItem>
                    <MenuItem value="CONSULTANT">{t('reports.providerType.CONSULTANT')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(_, newValue) => setTabValue(newValue)}
            aria-label={t('reports.aria.reportTabs')}
          >
            <Tab icon={<OverviewIcon />} iconPosition="start" label={t('reports.tabs.overview')} />
            <Tab icon={<MoneyIcon />} iconPosition="start" label={t('reports.tabs.financial')} />
            <Tab icon={<OperationsIcon />} iconPosition="start" label={t('reports.tabs.operations')} />
            <Tab icon={<DoctorsIcon />} iconPosition="start" label={t('reports.tabs.doctors')} />
            <Tab icon={<PatientsIcon />} iconPosition="start" label={t('reports.tabs.customers')} />
            <Tab icon={<ServicesIcon />} iconPosition="start" label={t('reports.tabs.services')} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <OverviewReport period={period} />
        </TabPanel>

        {/* Financial Report */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">{t('reports.sections.financialReport')}</Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={exportingFinancial}
              >
                {exportingFinancial ? <CircularProgress size={20} /> : t('reports.buttons.export')}
              </Button>
            </Box>

            <MoneySummary period={period} />

            {financialLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : financialData?.data ? (
              <FinancialSections data={financialData.data} period={period} />
            ) : (
              <Typography color="text.secondary">{t('common.noData')}</Typography>
            )}
          </CardContent>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <OperationsReport period={period} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <DoctorsReport
            period={period}
            canExport={canExport}
            providerType={(providerType || undefined) as 'SPECIALIST' | 'CONSULTANT' | undefined}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <CustomersInsights period={period} />
          <Divider />
          <PatientsReport period={period} canExport={canExport} />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <ServicesPerformanceReport period={period} canExport={canExport} />
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ReportsPage;

